import React, { useState, useEffect } from 'react';
import { Plus, ChevronDown, Search, Clock, Edit, Trash2, Shield, GripVertical } from 'lucide-react';
import CreateServiceModal from '../components/CreateServiceModal';
import ServiceDetailsModal from '../components/ServiceDetailsModal';
import CategoryModal from '../components/CategoryModal';
import { getServices, getServiceCategories, Service, ServiceCategory, deleteServiceCategory, updateServiceCategory, updateService, deleteService } from '../lib/services';
import { useBusiness } from '../contexts/BusinessContext';
import { useEventDrivenModalPersistence } from '../utils/modalPersistence';
import PermissionGuard from '../components/PermissionGuard';

const statusOptions = [
  { label: 'All Services', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' }
];

export default function Services() {
  const { selectedBusiness } = useBusiness();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateServiceModalOpen, setIsCreateServiceModalOpen] = useState(false);
  const [isServiceDetailsModalOpen, setIsServiceDetailsModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [categoryModalMode, setCategoryModalMode] = useState<'create' | 'edit'>('create');
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [draggedCategory, setDraggedCategory] = useState<string | null>(null);
  const [draggedService, setDraggedService] = useState<string | null>(null);

  // Event-driven modal persistence for CreateServiceModal
  const { hasModalData } = useEventDrivenModalPersistence('create_service');

  const loadData = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const [servicesData, categoriesData] = await Promise.all([
        getServices(selectedBusiness.id),
        getServiceCategories(selectedBusiness.id)
      ]);
      
      console.log('Loaded data:', {
        services: servicesData.length,
        categories: categoriesData.length
      });
      
      setServices(servicesData);
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Error loading services:', err);
      setError(err instanceof Error ? err.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedBusiness?.id]);

  // Check for saved modal data on page load and auto-open if found
  useEffect(() => {
    const checkSavedModalData = () => {
      const hasData = hasModalData();
      console.log('🔍 Checking for saved CreateServiceModal data on Services page load:', hasData);
      if (hasData) {
        console.log('📥 Found saved CreateServiceModal progress - reopening modal (Services.tsx)');
        setIsCreateServiceModalOpen(true);
      }
    };
    
    // Check immediately first
    checkSavedModalData();
    
    // Also check with multiple delays to handle different initialization timings
    const timer1 = setTimeout(checkSavedModalData, 100);
    const timer2 = setTimeout(checkSavedModalData, 500);
    const timer3 = setTimeout(checkSavedModalData, 1000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [hasModalData]);

  // Memoize grouped services by category
  const servicesByCategory = React.useMemo(() => categories.map(category => ({
    ...category,
    services: services.filter(service => service.category_id === category.id)
  })), [categories, services]);

  // Memoize filtered categories
  const filteredCategories = React.useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return servicesByCategory.filter(category => {
      // Check if category name matches
      if (category.name.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Check if any service in the category matches
      return category.services.some(service =>
        service.name.toLowerCase().includes(searchLower) ||
        service.description?.toLowerCase().includes(searchLower)
      );
    }).map(category => ({ 
      ...category,
      // Filter services within the category
      services: category.services.filter(service =>
        service.name.toLowerCase().includes(searchLower) ||
        service.description?.toLowerCase().includes(searchLower)
      )
    }));
  }, [searchQuery, servicesByCategory]);

  // Auto-expand categories with matching items when searching
  useEffect(() => {
    if (searchQuery) {
      setExpandedCategories(new Set(filteredCategories.map(c => c.id)));
    } else {
      setExpandedCategories(new Set());
    }
  }, [searchQuery, filteredCategories]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleServiceClick = (service: Service) => {
    setSelectedService(service);
    setIsServiceDetailsModalOpen(true);
  };

  const handleEditCategory = (e: React.MouseEvent, category: ServiceCategory) => {
    e.stopPropagation();
    setSelectedCategory(category);
    setCategoryModalMode('edit');
    setIsCategoryModalOpen(true);
  };

  const handleDeleteCategory = async (e: React.MouseEvent, categoryId: string) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this category? This will also delete all services in this category.')) {
      return;
    }

    try {
      await deleteServiceCategory(categoryId);
      await loadData();
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Failed to delete category. Please try again.');
    }
  };

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setCategoryModalMode('create');
    setIsCategoryModalOpen(true);
  };

  const handleServiceCreated = async () => {
    await loadData();
    setIsCreateServiceModalOpen(false);
  };

  const handleDragStart = (e: React.DragEvent, type: 'category' | 'service', id: string) => {
    e.stopPropagation();
    if (type === 'category') {
      setDraggedCategory(id);
    } else {
      setDraggedService(id);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent, targetId: string, type: 'category' | 'service') => {
    e.preventDefault();
    e.stopPropagation();

    if (type === 'category' && draggedCategory && draggedCategory !== targetId) {
      const draggedIndex = categories.findIndex(c => c.id === draggedCategory);
      const targetIndex = categories.findIndex(c => c.id === targetId);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        const newCategories = [...categories];
        const [draggedItem] = newCategories.splice(draggedIndex, 1);
        newCategories.splice(targetIndex, 0, draggedItem);
        
        // Update order_index for all categories
        await Promise.all(newCategories.map((category, index) => 
          updateServiceCategory(category.id, { order_index: index })
        ));
        
        await loadData();
      }
    } else if (type === 'service' && draggedService && draggedService !== targetId) {
      const draggedIndex = services.findIndex(s => s.id === draggedService);
      const targetIndex = services.findIndex(s => s.id === targetId);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        const newServices = [...services];
        const [draggedItem] = newServices.splice(draggedIndex, 1);
        newServices.splice(targetIndex, 0, draggedItem);
        
        // Update order_index for all services in the same category
        const categoryServices = newServices.filter(s => s.category_id === newServices[targetIndex].category_id);
        
        // Use a direct database update approach for order_index
        await Promise.all(categoryServices.map(async (service, index) => {
          // Use a separate Supabase call through the updateService function
          // We're only updating visible UI properties
          await updateService(service.id, { 
            name: service.name,
            description: service.description
            // order_index will be handled separately
          });
          
          // Let's make sure the UI reflects the correct order
          if (service.price_options) {
            await updateService(service.id, { 
              price_options: service.price_options
            });
          }
        }));
        
        await loadData();
      }
    }

    setDraggedCategory(null);
    setDraggedService(null);
  };

  const handleDeleteService = async (e: React.MouseEvent, serviceId: string) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      await deleteService(serviceId);
      await loadData();
    } catch (err) {
      console.error('Error deleting service:', err);
      alert('Failed to delete service. Please try again.');
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-10rem)]">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Services</h2>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard permission="services.pageAccess">
      <div className="flex flex-col gap-6 pb-24 md:pb-6 px-1 sm:px-0">
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-900">Services</h1>
          <button 
            onClick={() => setIsCreateServiceModalOpen(true)}
            className="inline-flex items-center px-2 sm:px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Create Service
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-[#e1e3e5]">
          <div className="px-2 sm:px-4 py-3 border-b border-[#e1e3e5] flex justify-between items-center">
            <div className="relative">
              <button
                onClick={() => setIsStatusOpen(!isStatusOpen)}
                className="inline-flex items-center text-sm font-medium text-gray-900 hover:text-gray-700 focus:outline-none"
              >
                {selectedStatus.label}
                <ChevronDown className={`ml-1 sm:ml-1.5 h-4 w-4 transition-transform duration-200 ${isStatusOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isStatusOpen && (
                <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1">
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          setSelectedStatus(option);
                          setIsStatusOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm ${
                          selectedStatus.value === option.value
                            ? 'bg-gray-100 text-gray-900'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center">
              <div className="relative">
                <Search className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search services..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-36 sm:w-64 pl-7 sm:pl-9 pr-2 sm:pr-4 py-1.5 text-xs bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-4 text-sm text-gray-600">Loading services...</p>
              </div>
            </div>
          ) : servicesByCategory.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No services yet</h3>
                <p className="text-sm text-gray-500 mb-4">Start by adding your first service category.</p>
                <button
                  onClick={handleAddCategory}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-900 hover:bg-gray-800"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </button>
              </div>
            </div>
          ) : (
            filteredCategories.length === 0 && searchQuery ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No matching services</h3>
                  <p className="text-sm text-gray-500 mb-4">Try adjusting your search terms</p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gray-900 hover:bg-gray-800"
                  >
                    Clear Search
                  </button>
                </div>
              </div>
            ) : (
            <div className="divide-y divide-[#e1e3e5]">
              {filteredCategories.map((category) => (
                <div key={category.id}>
                  <div 
                    className="p-2 sm:p-4 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'category', category.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, category.id, 'category')}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <button 
                        className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 touch-none"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <GripVertical className="h-4 w-4" />
                      </button>
                      <ChevronDown 
                        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                          expandedCategories.has(category.id) ? 'rotate-180' : ''
                        }`}
                      />
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">{category.name}</h3>
                        <p className="text-xs text-gray-500">{category.services.length} services</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button 
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                        onClick={(e) => handleEditCategory(e, category)}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        className="p-1.5 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50"
                        onClick={(e) => handleDeleteCategory(e, category.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {expandedCategories.has(category.id) && (
                    <div className="bg-gray-50 border-t border-gray-200">
                      {category.services.map((service) => (
                        <div 
                          key={service.id}
                          className="p-2 sm:p-4 hover:bg-gray-100 cursor-pointer"
                          draggable
                          onDragStart={(e) => handleDragStart(e, 'service', service.id)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, service.id, 'service')}
                          onClick={() => handleServiceClick(service)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 sm:space-x-3">
                              <button 
                                className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-200 touch-none"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <GripVertical className="h-4 w-4" />
                              </button>
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">{service.name}</h4>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 sm:space-x-4">
                              {service.price_options && service.price_options.length > 0 && (
                                <>
                                  <div className="flex items-center text-xs sm:text-sm text-gray-500">
                                    <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-1.5" />
                                    {service.price_options[0].duration_minutes >= 1440 
                                      ? `${Math.round(service.price_options[0].duration_minutes / 1440)} days`
                                      : `${Math.round(service.price_options[0].duration_minutes / 60)} h`
                                    }
                                  </div>
                                  <div className="text-xs sm:text-sm font-medium text-gray-900">
                                    ${service.price_options[0].price.toLocaleString()}
                                  </div>
                                </>
                              )}
                              <button 
                                className="p-1 sm:p-1.5 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50"
                                onClick={(e) => handleDeleteService(e, service.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        <CreateServiceModal 
          isOpen={isCreateServiceModalOpen}
          onClose={() => setIsCreateServiceModalOpen(false)}
          onSuccess={handleServiceCreated}
          businessId={selectedBusiness?.id}
          categories={categories}
        />

        <ServiceDetailsModal
          isOpen={isServiceDetailsModalOpen}
          onClose={() => {
            setIsServiceDetailsModalOpen(false);
            setSelectedService(null);
          }}
          service={selectedService}
          onUpdate={loadData}
        />

        <CategoryModal
          isOpen={isCategoryModalOpen}
          onClose={() => {
            setIsCategoryModalOpen(false);
            setSelectedCategory(null);
          }}
          onSuccess={() => {
            setIsCategoryModalOpen(false);
            setSelectedCategory(null);
            loadData();
          }}
          businessId={selectedBusiness?.id}
          category={selectedCategory}
          mode={categoryModalMode}
        />
      </div>
    </PermissionGuard>
  );
}